import { motion } from 'motion/react';
import { cn } from '@/lib/cn';

/**
 * FOCTTA brand mark - Phase 0 item 9 (2026-05-26).
 *
 * REPLACED the previous custom hexagonal-shield inline SVG. The prior
 * implementation drew a hex shape with a custom HSL gradient that did
 * NOT match the FOCTTA Brand Guidelines (PDF page 9 - "do not distort
 * the shorthand" and "do not change colour of elements" are explicit
 * brand-misuse violations).
 *
 * Now uses the canonical brand assets bridged from FOCTTA Core
 * (G:\VaultConsent\apps\web\public\foctta-*). Two-img light/dark swap
 * via Tailwind `dark:` variant - same pattern used in Core's side-nav.
 *
 * Signature kept backwards-compatible with all existing callers:
 *   <Mark size={32} />                - icon variant, default
 *   <Mark size={32} animated />       - icon with mount fade-in
 *   <Mark size={32} className="..." />- pass-through className
 * Plus new optional variant prop for the wordmark version.
 */

type MarkVariant = 'icon' | 'wordmark';

export interface MarkProps {
  variant?: MarkVariant;
  /** Visual size in pixels. For `icon`, picks the right PNG bucket. */
  size?: number;
  className?: string;
  /** Mount fade-in animation (preserves prior visual feel). */
  animated?: boolean;
  /** Alt text override (default: "FOCTTA"). */
  alt?: string;
}

export function Mark({
  variant = 'icon',
  size = 32,
  className,
  animated = false,
  alt = 'FOCTTA',
}: MarkProps): JSX.Element {
  const Wrapper = animated ? motion.span : 'span';
  const animationProps = animated
    ? {
        initial: { scale: 0.95, opacity: 0 },
        animate: { scale: 1, opacity: 1 },
        transition: { type: 'spring' as const, stiffness: 280, damping: 22 },
      }
    : {};

  // 2026-05-27 - brand assets refreshed to the canonical PNGs supplied
  // by the FOCTTA brand team. Two pairs:
  //   /foctta-brand-icon-{light,dark}.png        - square icon mark
  //   /foctta-brand-horizontal-{light,dark}.png  - horizontal wordmark
  // dark:hidden / hidden dark:block swaps between them based on theme.
  if (variant === 'wordmark') {
    // 2026-05-27 - uses Core's proven horizontal wordmark assets
    // (/foctta-logo-{light,dark}-200.png), which have true alpha
    // transparency, a clean 5:1 inline layout (FOCTTA + tagline on
    // one row), and look great on Core's side-nav. The brand-team-
    // supplied `foctta-brand-horizontal-*.png` files turned out to
    // have awkward layouts (cropped tagline words, F icon offset
    // into a corner), so the originals win on visual quality.
    // 2026-07-11 - founder-supplied brand assets (G:\VaultConsent\docs\Foctta
    // image), web-sized into public/. Lockup = square F-mark + the "FOCTTA"
    // wordmark (Core's tagline dropped) + a "Discovery" product label, so the
    // Discovery side-nav reads "FOCTTA Discovery". The wordmark swaps
    // light/dark; the mark + product label are theme-styled inline.
    return (
      <Wrapper
        className={cn('inline-flex items-center', className)}
        style={{ gap: size * 0.3 }}
        {...animationProps}
      >
        <img
          src="/foctta-mark.png"
          alt=""
          className="w-auto shrink-0"
          style={{ height: size }}
        />
        {/* "FOCTTA" wordmark with the "Discovery" product label stacked
            beneath it - compact for the narrow side-nav, reads
            "FOCTTA Discovery". */}
        <span className="inline-flex flex-col justify-center" style={{ gap: size * 0.12 }}>
          <img
            src="/foctta-wordmark-light.png"
            alt={alt}
            className="w-auto dark:hidden"
            style={{ height: size * 0.5 }}
          />
          <img
            src="/foctta-wordmark-dark.png"
            alt=""
            className="w-auto hidden dark:block"
            style={{ height: size * 0.5 }}
          />
          <span
            className="font-semibold uppercase text-[#ef6a2b] whitespace-nowrap"
            style={{ fontSize: size * 0.32, letterSpacing: '0.14em', lineHeight: 1 }}
          >
            Discovery
          </span>
        </span>
      </Wrapper>
    );
  }

  // icon variant - the square F-mark (orange/blue works on both themes).
  return (
    <Wrapper
      className={cn('inline-flex items-center justify-center', className)}
      {...animationProps}
    >
      <img
        src="/foctta-mark.png"
        alt={alt}
        className="object-contain"
        style={{ width: size, height: size }}
      />
    </Wrapper>
  );
}

/**
 * Wordmark - "FOCTTA" text label with optional tagline.
 *
 * Phase 0 item 9 (2026-05-26) - kept as a text-based wordmark for
 * callers that need an inline text label rather than the PNG wordmark.
 * Use `<Mark variant="wordmark" size={28} />` for the PNG version.
 *
 * Tagline updated from "Privacy · Trust · Compliance" to the official
 * brand voice line per the FOCTTA Brand Guidelines PDF page 3.
 */
export function Wordmark({
  tagline = false,
  className,
}: {
  tagline?: boolean;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col leading-none', className)}>
      <span className="font-display font-bold text-base tracking-tight">FOCTTA</span>
      {tagline ? (
        <span className="text-[10px] text-muted-foreground tracking-wide mt-0.5">
          Orchestrating privacy, trust and compliance
        </span>
      ) : null}
    </div>
  );
}
